import React from 'react';
import type { FC } from 'react';
import { useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import type { Snippet } from '../types/snippet.ts';
import { wrapAdfWithDocNode } from '../utils/adfUtils.ts';
import { getSuggestedDuplicateName } from '../utils/duplicateUtils.ts';
import { useSnippetsActions, useSnippetsState } from '../store/snippetsStore.ts';
import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
	MODAL_OUTCOME,
	MODAL_TYPE,
} from '../constants/snippetAnalytics.ts';
import type { AnalyticsOrigin } from '../constants/snippetAnalytics.ts';
import { useOpenSnippetPanel } from '../hooks/panel/useOpenSnippetPanel.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';

import { getSnippetBaseModalProps, SnippetBaseModal } from './base/SnippetBaseModal.tsx';
import { messages as baseMessages } from './base/SnippetBaseModal.i18n.ts';
import { isBodyTooLargeError } from '../services/types/blockServiceTypes.ts';
import type { SnippetBaseModalFormValues } from './utils/snippetModalFormUtils.ts';
import { getDefaultFormValues } from './utils/snippetModalFormUtils.ts';
import { useSnippetModalClose } from '../hooks/modals/useSnippetModalClose.ts';
import { duplicateSnippetModalMessages } from './DuplicateSnippetModal.i18n.ts';

export type DuplicateSnippetModalProps = {
	snippet: Snippet;
	onClose: () => void;
	onCloseComplete: () => void;
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
	analyticsOrigin: AnalyticsOrigin;
} & WithFlagsProps;

const DuplicateSnippetModalWithFlags: FC<DuplicateSnippetModalProps> = ({
	snippet,
	onClose,
	onCloseComplete,
	contentId,
	spaceId,
	spaceKey,
	flags,
	editorAPI,
	analyticsOrigin,
}) => {
	const intl = useIntl();
	const openSnippetPanel = useOpenSnippetPanel();
	const { isSnippetSharingForSiteEnabled = false } = useSnippetsState();
	const snippetsActions = useSnippetsActions();
	const { fireFailedAnalytic, fireSuccessAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.DUPLICATE_SNIPPET_MODAL,
	);
	const handleClose = useSnippetModalClose(
		onClose,
		ANALYTICS_ORIGIN.DUPLICATE_SNIPPET_MODAL,
		ANALYTICS_UI_SUBJECT_ID.DUPLICATE_SNIPPET,
	);

	const baseValues = getDefaultFormValues(isSnippetSharingForSiteEnabled);
	const suggestedName = getSuggestedDuplicateName(snippet.name ?? '');
	const defaultValues: SnippetBaseModalFormValues = {
		...baseValues,
		snippetName: suggestedName,
		snippetDescription: snippet.description ?? '',
	};

	const initialAdf = wrapAdfWithDocNode(snippet?.body);
	const formKey = `duplicate-${snippet.snippetId}`;
	const title = intl.formatMessage(duplicateSnippetModalMessages.titleDuplicate, {
		snippetName: snippet.name ?? '',
	});
	const submitLabel = intl.formatMessage(duplicateSnippetModalMessages.duplicateButton);

	const successFlagActions =
		analyticsOrigin === ANALYTICS_ORIGIN.SNIPPET_GALLERY
			? undefined
			: [
					{
						content: intl.formatMessage(baseMessages.successFlagViewTemplateAction),
						onClick: openSnippetPanel,
					},
				];

	const onSubmit = async (values: SnippetBaseModalFormValues, currentAdf: DocNode) => {
		const bodySize = JSON.stringify(currentAdf).length;
		const duplicatedSnippet = await snippetsActions.createSnippet(
			values.snippetName,
			currentAdf,
			values.snippetDescription,
			{
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				isShared: values.isShared,
				successFlagActions,
				onShareFailure: (snippetId: string) =>
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SHARE, { snippetId }),
				onSaveFailure: (error: unknown) => {
					const isBodyTooLarge = isBodyTooLargeError(error);
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_DUPLICATE, {
						snippetId: snippet.snippetId,
						...(isBodyTooLarge && { isBodyTooLarge: true, bodySize }),
					});
				},
				messages: {
					successFlagTitle: duplicateSnippetModalMessages.duplicateSuccessFlagTitle,
					errorFlagTitle: duplicateSnippetModalMessages.duplicateErrorFlagTitle,
					errorFlagDescription: duplicateSnippetModalMessages.duplicateErrorFlagDescription,
				},
			},
		);
		if (duplicatedSnippet) {
			fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SAVE_SUCCESS, {
				snippetId: duplicatedSnippet.snippetId,
				bodySize,
				modalType: MODAL_TYPE.DUPLICATE,
			});
			handleClose({
				outcome: MODAL_OUTCOME.DUPLICATED,
				snippetId: duplicatedSnippet.snippetId,
			});
		}
	};

	return (
		<SnippetBaseModal
			{...getSnippetBaseModalProps({
				title,
				submitLabel,
				formKey,
				defaultValues,
				initialAdf,
				onSubmit,
				handleClose,
				fireFailedAnalytic,
				onCloseComplete,
				contentId,
				spaceId,
				spaceKey,
				snippetId: snippet.snippetId,
				snippetScope: snippet.scope,
				isSnippetSharingForSiteEnabled,
			})}
		/>
	);
};

export const DuplicateSnippetModal = withFlags(DuplicateSnippetModalWithFlags);
